// =====================================================
// MINISTER SEARCH HOOK
// Stage 2: Participants & Pricing
// Debounced search for ministers with filtering
// =====================================================

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/supabase.config';

// ===================
// TYPES
// ===================

interface MinisterSearchResult {
  id: string;
  adb_code: string;
  full_name: string | null;
  dni: string;
  district_id: string | null;
  region_id: string | null;
  degree_id: string | null;
  position: string | null;
  birth_date: string | null;
}

// ===================
// SEARCH FUNCTION
// ===================

async function searchMinisters(query: string): Promise<MinisterSearchResult[]> {
  if (!query || query.length < 2) {
    return [];
  }

  const searchLower = query.toLowerCase();

  const { data, error } = await supabase
    .from('ministers')
    .select('id, adb_code, full_name, dni, district_id, region_id, degree_id, position, birth_date')
    .or(`full_name.ilike.%${searchLower}%,adb_code.ilike.%${searchLower}%,dni.ilike.%${searchLower}%`)
    .eq('is_active', true)
    .order('full_name', { ascending: true })
    .limit(20);

  if (error) {
    console.error('Error searching ministers:', error);
    throw new Error('Error al buscar ministros');
  }

  return data || [];
}

// ===================
// HOOK
// ===================

/**
 * Hook for debounced minister search
 */
export function useMinisterSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const query = useQuery({
    queryKey: ['ministerSearch', debouncedQuery],
    queryFn: () => searchMinisters(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    searchQuery,
    setSearchQuery,
    ministers: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
